import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import {
  Home,
  MessageSquare,
  Calendar,
  ClipboardCheck,
  Settings,
  Building2,
  LayoutDashboard,
  LogOut,
} from "lucide-react";

const SIDEBAR_W = "lg:pl-64";

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [features, setFeatures] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!user?.selected_hotel) return;
      try {
        const res = await axios.get(`${API}/hotels`);
        const h = res.data.find((x) => x.slug === user.selected_hotel);
        if (h) setFeatures(h.features || {});
      } catch {}
    };
    load();
  }, [user?.selected_hotel]);

  const isManager = ["super_admin", "admin", "management"].includes(user?.role);
  const canUseChecklist =
    (features?.checklist) &&
    (["front_office", "night_audit", "management"].includes(user?.department) || isManager);
  const canSeeEvents = isManager || user?.department === "meeting_events";

  const navItems = [
    { to: "/", label: "Homepage", icon: Home, show: true },
    { to: "/chat", label: "Chat", icon: MessageSquare, show: true },
    { to: "/events", label: "Eventi", icon: Calendar, show: canSeeEvents },
    { to: "/checklist", label: "Check List", icon: ClipboardCheck, show: canUseChecklist },
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: user?.role === "super_admin" },
    { to: "/admin", label: "Impostazioni", icon: Settings, show: isManager },
    { to: "/hotels", label: "Hotel", icon: Building2, show: user?.role === "super_admin" },
  ].filter((i) => i.show);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/chat") return location.pathname === "/chat";
    return location.pathname.startsWith(to);
  };
  const hideBottomNav = location.pathname.startsWith("/chat/");

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-stone-50 lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-stone-900 text-stone-200 z-40" data-testid="desktop-sidebar">
        <div className="px-6 py-6">
          <h1 className="font-outfit text-2xl font-bold text-white tracking-tight">
            End<span className="text-amber-500">•</span>ver
          </h1>
          <p className="text-xs text-stone-400 mt-1 truncate">{user?.name}</p>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active ? "bg-amber-500 text-stone-900" : "text-stone-300 hover:bg-stone-800 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-stone-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-stone-300 hover:bg-stone-800 hover:text-white transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5" />
            Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 min-w-0 ${SIDEBAR_W} ${hideBottomNav ? "" : "pb-16 lg:pb-0"}`}>
        {children}
      </div>

      {/* Mobile bottom nav */}
      {!hideBottomNav && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 z-40 flex" data-testid="bottom-nav">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
                  active ? "text-amber-600" : "text-stone-400"
                }`}
                data-testid={`bottomnav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export default AppShell;
